"use client";

import { debugLog } from "@/lib/debug-log";
import { telemetry } from "@/lib/telemetry";
import type { UIMessage } from "ai";
import cx from "classnames";
import { AnimatePresence, motion } from "framer-motion";
import { memo, MutableRefObject, useEffect, useMemo, useRef, useState } from "react";
import type { Vote } from "@/lib/db/schema";
import { ToolDisplay } from "./tool-displays";
import { Markdown } from "./markdown";
import { SkillArtifactCard } from "./skills/SkillArtifactCard";
import { MessageActions } from "./message-actions";
import { cn, sanitizeText } from "@/lib/utils";
import { isNotebookBusy } from "@/lib/tool-utils";
import { GenericError } from "./tool-displays/generic-error";
import { effectiveViewModeAtTool } from "./tool-displays/effective-view-mode";
import type { TradingChartRef } from "./TradingChart/TradingChart";
import { MessageReasoning } from "./message-reasoning";
import type { UseChatHelpers } from "@ai-sdk/react";
import equal from "fast-deep-equal";
import { Check, ChevronDown, GitFork, Loader2, PanelRightOpen } from "lucide-react";
import { PreviewAttachment } from "./preview-attachment";
import { ErrorCard } from "./error-card";
import { useArtifact } from "@/hooks/use-artifact";

const RELEASE_TAG = "<release/>";
const TOOL_CALL_PLACEHOLDER = "[Tool call]";
const DEBUG_STREAM_ORDER_SESSION_ID = "59716b15-cdaa-48ef-9991-4ef40c11129c";
const PLAN_TOOL_NAMES = new Set([
  "consult_planning_agent",
  "create_plan",
  "update_plan",
  "get_plan",
]);
const TELEMETRY_SUPPORTED_TOOLS = new Set([
  "add_cell",
  "replace_cell",
  "edit_cell",
  "delete_cells",
  "restart_notebook",
  "create_plan",
  "update_plan",
  "unified_search",
  "get_documentation",
  "get_visual_templates",
  "get_cell_details",
  "create_new_run",
  "execute_all_cells",
  "add_plan_step",
  "delete_plan_step",
  "add_drawings",
  "set_viewport",
  "delete_drawings",
  "fast_forward",
  "await_user_execution",
  "ask_user",
  "user_message",
  "ticker_search",
  "error_logging_tool",
  "generate_summary",
  "get_xynth_market_screener_docs",
  "consult_planning_agent",
  "search_docs",
  "upload_alert",
  "delete_alert",
  "edit_alert",
  "fork_alert",
  "create_alert",
  "switch_mode",
  "propose_skill",
  "get_skill",
]);

const USER_MESSAGE_COLLAPSE_CHARACTER_THRESHOLD = 1200;
const USER_MESSAGE_COLLAPSE_LINE_THRESHOLD = 14;
const USER_MESSAGE_COLLAPSED_CHARACTER_LIMIT = 700;
const USER_MESSAGE_COLLAPSED_LINE_LIMIT = 10;

type IndexedPart = { part: any; index: number };
type LiveDisplayState = {
  displayParts: any[];
  rawPartKeys: string[];
  lastRawText: string;
  activeTextIndex: number | null;
};
type ExtractedPlanStep = {
  id: string;
  description: string;
  status: string;
};
type ThinkingSection = {
  id: string;
  title: string;
  parts: IndexedPart[];
  isComplete: boolean;
};

function cleanAssistantText(text: string): string {
  return text
    .replaceAll(RELEASE_TAG, "")
    .replaceAll(TOOL_CALL_PLACEHOLDER, "")
    .trim();
}

function shouldCollapseUserMessageText(text: string): boolean {
  if (text.length > USER_MESSAGE_COLLAPSE_CHARACTER_THRESHOLD) {
    return true;
  }

  let lineBreakCount = 0;
  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i);
    if (code !== 10 && code !== 13) continue;

    lineBreakCount++;
    if (code === 13 && text.charCodeAt(i + 1) === 10) i++;
    if (lineBreakCount >= USER_MESSAGE_COLLAPSE_LINE_THRESHOLD) {
      return true;
    }
  }

  return false;
}

function getCollapsedUserMessageText(text: string): string {
  let endIndex = Math.min(text.length, USER_MESSAGE_COLLAPSED_CHARACTER_LIMIT);
  let lineBreakCount = 0;

  for (let i = 0; i < endIndex; i++) {
    const code = text.charCodeAt(i);
    if (code !== 10 && code !== 13) continue;

    lineBreakCount++;
    if (lineBreakCount >= USER_MESSAGE_COLLAPSED_LINE_LIMIT) {
      endIndex = i;
      break;
    }

    if (code === 13 && text.charCodeAt(i + 1) === 10) i++;
  }

  const charLimited = text.slice(0, endIndex);

  return `${charLimited.trimEnd()}…`;
}

function cleanIndexedPart(part: any, index: number): IndexedPart {
  if (part?.type !== "text" || typeof part.text !== "string") {
    return { part, index };
  }

  return {
    part: {
      ...part,
      text: cleanAssistantText(part.text),
    },
    index,
  };
}

function stripReleaseTagFromParts(parts: any[]): IndexedPart[] {
  return parts
    .map((part, index) => cleanIndexedPart(part, index))
    .filter(({ part }) => part?.type !== "text" || part.text);
}

function stripReleaseTagFromIndexedParts(parts: IndexedPart[]): IndexedPart[] {
  return parts
    .map(({ part, index }) => cleanIndexedPart(part, index))
    .filter(({ part }) => part?.type !== "text" || part.text);
}

function cloneDisplayPart(part: any, displayPartKey?: string): any {
  const withDisplayKey = (cloned: any) =>
    displayPartKey ? { ...cloned, _displayPartKey: displayPartKey } : cloned;

  if (part?.type === "tool-invocation") {
    return withDisplayKey({
      ...part,
      toolInvocation: {
        ...part.toolInvocation,
      },
    });
  }

  if (part?.type === "reasoning" && Array.isArray(part.details)) {
    return withDisplayKey({
      ...part,
      details: part.details.map((detail: any) => ({ ...detail })),
    });
  }

  return withDisplayKey({ ...part });
}

function getRawPartKey(part: any, index: number): string {
  if (part?.type === "text") return `text:${index}`;
  if (part?.type === "tool-invocation") {
    return `tool:${part?.toolInvocation?.toolCallId ?? index}`;
  }
  return `${String(part?.type ?? "unknown")}:${index}`;
}

function getReasoningSignature(part: any): string | null {
  if (part?.type !== "reasoning") return null;

  const reasoning = String(part.reasoning ?? "").trim();
  if (reasoning) return reasoning;

  if (!Array.isArray(part.details) || part.details.length === 0) return null;
  return JSON.stringify(
    part.details.map((detail: any) => ({
      type: detail?.type,
      text: typeof detail?.text === "string" ? detail.text.trim() : undefined,
      data: typeof detail?.data === "string" ? detail.data : undefined,
    })),
  );
}

function dedupeReasoningDisplayParts(parts: any[]): {
  displayParts: any[];
  removedReasoningDuplicates: number;
} {
  const seenReasoning = new Set<string>();
  let removedReasoningDuplicates = 0;
  const displayParts: any[] = [];

  for (const part of parts) {
    const signature = getReasoningSignature(part);
    if (signature) {
      if (seenReasoning.has(signature)) {
        removedReasoningDuplicates++;
        continue;
      }
      seenReasoning.add(signature);
    }

    displayParts.push(part);
  }

  return { displayParts, removedReasoningDuplicates };
}

function isPrefix(prefix: string[], full: string[]): boolean {
  if (prefix.length > full.length) return false;
  for (let i = 0; i < prefix.length; i++) {
    if (prefix[i] !== full[i]) return false;
  }
  return true;
}

function hasSamePartLayout(currentParts: any[], nextParts: any[]): boolean {
  if (currentParts.length !== nextParts.length) return false;

  for (let i = 0; i < currentParts.length; i++) {
    const current = currentParts[i];
    const next = nextParts[i];

    if (current?.type !== next?.type) return false;

    if (current?.type === "text" && current?.text !== next?.text) {
      return false;
    }

    if (current?.type === "tool-invocation") {
      if (
        current?.toolInvocation?.toolCallId !== next?.toolInvocation?.toolCallId ||
        current?.toolInvocation?.state !== next?.toolInvocation?.state
      ) {
        return false;
      }
    }
  }

  return true;
}

const _liveDisplayStateCache = new Map<string, LiveDisplayState>();

function buildLiveDisplayParts(
  rawParts: any[],
  previousState?: LiveDisplayState,
  shouldBuild: boolean = false,
): {
  displayParts: any[];
  changed: boolean;
  nextState?: LiveDisplayState;
  removedReasoningDuplicates?: number;
  debug?: {
    rawPartKeys: string[];
    rawText: string;
    delta: string;
    activeTextIndexBefore: number | null;
    activeTextIndexAfter: number | null;
    preservedRawOrder?: boolean;
    preservedRawOrderReason?: string;
  };
} {
  const textParts = rawParts.filter((part) => part?.type === "text");
  const rawText = textParts.map((part) => part?.text ?? "").join("");
  const rawPartKeys = rawParts.map((part, index) => getRawPartKey(part, index));

  if (!shouldBuild) {
    const deduped = dedupeReasoningDisplayParts(rawParts);
    return {
      displayParts: deduped.displayParts,
      changed: deduped.removedReasoningDuplicates > 0,
      nextState: undefined,
      removedReasoningDuplicates: deduped.removedReasoningDuplicates,
    };
  }

  if (textParts.length > 1) {
    const stateParts = rawParts.map((part, index) =>
      cloneDisplayPart(part, getRawPartKey(part, index)),
    );
    const deduped = dedupeReasoningDisplayParts(stateParts);
    return {
      displayParts: deduped.displayParts,
      changed: deduped.removedReasoningDuplicates > 0,
      nextState: {
        displayParts: deduped.displayParts.map((part) => cloneDisplayPart(part)),
        rawPartKeys,
        lastRawText: rawText,
        activeTextIndex: null,
      },
      removedReasoningDuplicates: deduped.removedReasoningDuplicates,
    };
  }

  const shouldReset =
    !previousState ||
    !rawText.startsWith(previousState.lastRawText);

  const hasStepStartMarkers = rawParts.some(
    (part) => part?.type === "step-start",
  );
  const firstMeaningfulPart = rawParts.find(
    (part) => part?.type !== "step-start",
  );
  const startsWithToolInvocation =
    firstMeaningfulPart?.type === "tool-invocation";

  const baseState: LiveDisplayState = shouldReset
    ? {
        displayParts: [],
        rawPartKeys: [],
        lastRawText: "",
        activeTextIndex: null,
      }
    : previousState;

  if (hasStepStartMarkers) {
    const stateParts = rawParts.map((part, index) =>
      cloneDisplayPart(part, getRawPartKey(part, index)),
    );
    const deduped = dedupeReasoningDisplayParts(stateParts);
    return {
      displayParts: deduped.displayParts,
      changed: deduped.removedReasoningDuplicates > 0,
      nextState: {
        displayParts: deduped.displayParts.map((part) => cloneDisplayPart(part)),
        rawPartKeys,
        lastRawText: rawText,
        activeTextIndex: null,
      },
      removedReasoningDuplicates: deduped.removedReasoningDuplicates,
      debug: {
        rawPartKeys,
        rawText,
        delta: "",
        activeTextIndexBefore: previousState?.activeTextIndex ?? null,
        activeTextIndexAfter: null,
        preservedRawOrder: true,
        preservedRawOrderReason: "step_markers_present",
      },
    };
  }

  if (shouldReset && startsWithToolInvocation) {
    const stateParts = rawParts.map((part, index) =>
      cloneDisplayPart(part, getRawPartKey(part, index)),
    );
    const deduped = dedupeReasoningDisplayParts(stateParts);
    return {
      displayParts: deduped.displayParts,
      changed: deduped.removedReasoningDuplicates > 0,
      nextState: {
        displayParts: deduped.displayParts.map((part) => cloneDisplayPart(part)),
        rawPartKeys,
        lastRawText: rawText,
        activeTextIndex: null,
      },
      removedReasoningDuplicates: deduped.removedReasoningDuplicates,
      debug: {
        rawPartKeys,
        rawText,
        delta: "",
        activeTextIndexBefore: previousState?.activeTextIndex ?? null,
        activeTextIndexAfter: null,
        preservedRawOrder: true,
        preservedRawOrderReason: "reset_starts_with_tool",
      },
    };
  }

  const displayParts = baseState.displayParts.map((part) => cloneDisplayPart(part));
  const activeTextIndexBefore = baseState.activeTextIndex;
  let activeTextIndex = baseState.activeTextIndex;
  const toolIndexById = new Map<string, number>();
  const reasoningIndexByKey = new Map<string, number>();

  displayParts.forEach((part, index) => {
    if (part?.type === "tool-invocation" && part?.toolInvocation?.toolCallId) {
      toolIndexById.set(part.toolInvocation.toolCallId, index);
    } else if (part?.type === "reasoning" && part?._displayPartKey) {
      reasoningIndexByKey.set(part._displayPartKey, index);
    }
  });

  rawParts.forEach((part, index) => {
    const rawPartKey = getRawPartKey(part, index);

    if (part?.type === "tool-invocation") {
      const toolCallId = part?.toolInvocation?.toolCallId;
      if (!toolCallId) return;
      const existingIndex = toolIndexById.get(toolCallId);
      if (existingIndex !== undefined) {
        displayParts[existingIndex] = cloneDisplayPart(part, rawPartKey);
      }
      return;
    }

    if (part?.type === "reasoning") {
      const existingIndex = reasoningIndexByKey.get(rawPartKey);
      if (existingIndex !== undefined) {
        displayParts[existingIndex] = cloneDisplayPart(part, rawPartKey);
      }
    }
  });

  const delta = rawText.slice(baseState.lastRawText.length);
  if (delta) {
    if (
      activeTextIndex !== null &&
      activeTextIndex >= 0 &&
      activeTextIndex < displayParts.length &&
      displayParts[activeTextIndex]?.type === "text"
    ) {
      displayParts[activeTextIndex] = {
        ...displayParts[activeTextIndex],
        text: `${displayParts[activeTextIndex]?.text ?? ""}${delta}`,
      };
    } else {
      displayParts.push({ type: "text", text: delta });
      activeTextIndex = displayParts.length - 1;
    }
  }

  const existingToolIds = new Set(
    displayParts
      .filter((p) => p?.type === "tool-invocation" && p?.toolInvocation?.toolCallId)
      .map((p) => p.toolInvocation.toolCallId),
  );

  for (const [index, part] of rawParts.entries()) {
    const rawPartKey = getRawPartKey(part, index);
    if (part?.type === "text") continue;
    if (part?.type === "tool-invocation" && part?.toolInvocation?.toolCallId) {
      if (existingToolIds.has(part.toolInvocation.toolCallId)) continue;
      existingToolIds.add(part.toolInvocation.toolCallId);
    } else if (part?.type === "reasoning") {
      if (reasoningIndexByKey.has(rawPartKey)) continue;
      reasoningIndexByKey.set(rawPartKey, displayParts.length);
    } else if (part?.type === "step-start") {
      continue;
    }

    displayParts.push(cloneDisplayPart(part, rawPartKey));
    activeTextIndex = null;
  }

  const deduped = dedupeReasoningDisplayParts(displayParts);

  return {
    displayParts: deduped.displayParts,
    changed:
      deduped.removedReasoningDuplicates > 0 ||
      !hasSamePartLayout(rawParts, deduped.displayParts),
    nextState: {
      displayParts: deduped.displayParts,
      rawPartKeys,
      lastRawText: rawText,
      activeTextIndex,
    },
    removedReasoningDuplicates: deduped.removedReasoningDuplicates,
    debug: {
      rawPartKeys,
      rawText,
      delta,
      activeTextIndexBefore,
      activeTextIndexAfter: activeTextIndex,
      preservedRawOrder: false,
    },
  };
}

function isAllPlanStepsComplete(part: any): boolean {
  if (part?.type !== "tool-invocation") return false;
  const inv = part.toolInvocation;
  if (!PLAN_TOOL_NAMES.has(inv?.toolName)) return false;
  if (inv?.state !== "result") return false;
  const steps = extractPlanStepsFromToolResult(inv.result);
  return steps.length > 0 && steps.every((s) => s.status === "completed");
}

function findLastReleaseBlockingToolIndex(parts: IndexedPart[]): number {
  for (let i = parts.length - 1; i >= 0; i--) {
    const part = parts[i].part;
    if (part?.type !== "tool-invocation") continue;

    const toolName = part.toolInvocation?.toolName;
    if (
      toolName === "consult_planning_agent" ||
      toolName === "create_plan" ||
      toolName === "get_plan"
    ) {
      continue;
    }

    return i;
  }

  return -1;
}

function extractPlanStepsFromToolResult(result: any): ExtractedPlanStep[] {
  if (!result) return [];

  const mapSteps = (steps: any[]) =>
    steps
      .map((step: any, index: number) => ({
        id: String(step?.id ?? `${step?.description ?? "step"}-${index}`),
        description: String(step?.description ?? "").trim(),
        status: String(step?.status ?? "pending"),
      }))
      .filter((step) => step.description);

  const tryExtract = (data: any): ExtractedPlanStep[] => {
    if (!data) return [];

    if (Array.isArray(data?.plan)) {
      return mapSteps(data.plan);
    }

    if (Array.isArray(data?.steps)) {
      return mapSteps(data.steps);
    }

    if (typeof data?.plan === "string") {
      try {
        const parsed = JSON.parse(data.plan);
        if (Array.isArray(parsed?.steps)) {
          return mapSteps(parsed.steps);
        }
        if (Array.isArray(parsed)) {
          return mapSteps(parsed);
        }
      } catch {}
    }

    if (data?.plan && typeof data.plan === "object" && Array.isArray(data.plan.steps)) {
      return mapSteps(data.plan.steps);
    }

    return [];
  };

  const direct = tryExtract(result);
  if (direct.length > 0) return direct;

  if (Array.isArray(result?.content)) {
    const textContent = result.content.find((item: any) => item?.type === "text");
    if (typeof textContent?.text === "string") {
      try {
        const parsed = JSON.parse(textContent.text);
        const nested = tryExtract(parsed);
        if (nested.length > 0) return nested;
      } catch {}
    }
  }

  if (typeof result === "string") {
    try {
      const parsed = JSON.parse(result);
      const nested = tryExtract(parsed);
      if (nested.length > 0) return nested;
    } catch {}
  }

  return [];
}

function getActivePlanStep(steps: ExtractedPlanStep[]): ExtractedPlanStep | null {
  return (
    steps.find((step) => step.status === "in_progress") ??
    steps.findLast((step) => step.status === "completed") ??
    steps[0] ??
    null
  );
}

function cleanStepTitle(raw: string): string {
  let t = raw.trim();
  const usingIdx = t.search(/\busing\b/i);
  if (usingIdx > 8) t = t.slice(0, usingIdx).trim();
  const commaIdx = t.indexOf(",");
  if (commaIdx > 8) t = t.slice(0, commaIdx).trim();
  const parenIdx = t.indexOf("(");
  if (parenIdx > 8) t = t.slice(0, parenIdx).trim();
  if (t.length > 60) t = t.slice(0, 57) + "…";
  return t || raw.slice(0, 57);
}

function getFallbackSectionTitle(part: any): string | null {
  if (part?.type !== "tool-invocation") return null;

  const invocation = part.toolInvocation;
  const toolName = invocation?.toolName;
  const args = invocation?.args ?? {};
  const result = invocation?.result;
  const purpose =
    args?.purpose ??
    result?.execution_result?.cell_info?.purpose ??
    result?.cell_info?.purpose;

  if (typeof purpose === "string" && purpose.trim()) {
    return cleanStepTitle(purpose);
  }

  if (toolName === "search_docs") return "Finding the right data";
  if (toolName === "ticker_search") return "Resolving symbols";
  if (toolName === "consult_planning_agent") return "Designing the analysis";
  if (toolName === "edit_cell") return "Refining analysis";
  if (toolName === "add_cell" || toolName === "replace_cell") return "Running analysis";
  if (toolName === "restart_notebook") return "Restarting notebook";
  if (toolName === "unified_search") return "Searching the market";

  return null;
}

function hasRealWork(parts: IndexedPart[]): boolean {
  return parts.some(({ part }) => {
    if (part?.type === "tool-invocation") {
      const tn = part.toolInvocation?.toolName;
      return !PLAN_TOOL_NAMES.has(tn) || tn === "consult_planning_agent";
    }
    if (part?.type === "text" && typeof part.text === "string" && part.text.trim()) return true;
    return false;
  });
}

function buildThinkingSections(
  parts: IndexedPart[],
  isReleased: boolean,
  isMessageStreaming: boolean
): ThinkingSection[] {
  const raw: Array<{
    id: string;
    title: string;
    parts: IndexedPart[];
    stepKey: string | null;
  }> = [];

  let current = {
    id: "ts-0",
    title: "Working",
    parts: [] as IndexedPart[],
    stepKey: null as string | null,
  };

  const pushCurrent = () => {
    if (current.parts.length === 0) return;
    raw.push(current);
  };

  for (const item of parts) {
    const toolInvocation =
      item.part?.type === "tool-invocation" ? item.part.toolInvocation : null;
    const toolName = toolInvocation?.toolName;
    const steps =
      toolInvocation?.state === "result" && PLAN_TOOL_NAMES.has(toolName)
        ? extractPlanStepsFromToolResult(toolInvocation.result)
        : [];
    const activeStep = steps.length > 0 ? getActivePlanStep(steps) : null;

    if (activeStep) {
      const stepKey = activeStep.id || activeStep.description;

      if (!current.stepKey && current.parts.length === 0) {
        current = { ...current, title: cleanStepTitle(activeStep.description), stepKey };
      } else if (current.stepKey !== stepKey) {
        pushCurrent();
        current = {
          id: `ts-${raw.length}`,
          title: cleanStepTitle(activeStep.description),
          parts: [],
          stepKey,
        };
      } else {
        current = { ...current, title: cleanStepTitle(activeStep.description), stepKey };
      }

      if (PLAN_TOOL_NAMES.has(toolName) && toolName !== "consult_planning_agent") continue;
    }

    if (!current.stepKey && current.title === "Working") {
      const fallbackTitle = getFallbackSectionTitle(item.part);
      if (fallbackTitle) current = { ...current, title: fallbackTitle };
    }

    current.parts.push(item);
  }

  pushCurrent();

  // Merge consecutive sections where the earlier one has no real work
  const merged: typeof raw = [];
  for (const section of raw) {
    if (merged.length > 0 && !hasRealWork(merged[merged.length - 1].parts)) {
      const prev = merged[merged.length - 1];
      merged[merged.length - 1] = {
        ...prev,
        title: section.title,
        parts: [...prev.parts, ...section.parts],
        stepKey: section.stepKey,
      };
    } else {
      merged.push(section);
    }
  }

  // Drop trailing section if it has no real work (empty last window)
  if (merged.length > 0 && !hasRealWork(merged[merged.length - 1].parts)) {
    merged.pop();
  }

  return merged.map((section, index) => ({
    id: `ts-${index}`,
    title: section.title,
    parts: section.parts,
    isComplete: !isMessageStreaming || isReleased || index < merged.length - 1,
  }));
}

/**
 * Find the split point in message parts where <release/> appears.
 * Returns { confinedParts, releasedParts } where confinedParts go in the
 * scrollable thinking container and releasedParts render at full size.
 *
 * If no <release/> is found and the message is still streaming with tool work,
 * ALL parts go into confinedParts (still thinking).
 * If no <release/> is found and the message is done, everything is released
 * (graceful fallback — agent forgot the tag).
 */
function splitAtRelease(
  parts: any[],
  isStreaming: boolean,
  hasToolWork: boolean,
  disableConfinement: boolean
): { confinedParts: { part: any; index: number }[]; releasedParts: { part: any; index: number }[] } {
  if (disableConfinement) {
    return { confinedParts: [], releasedParts: stripReleaseTagFromParts(parts) };
  }

  const indexed = parts.map((part, index) => ({ part, index }));
  const messageHasReleaseTag = parts.some(
    (p: any) => p?.type === "text" && typeof p.text === "string" && p.text.includes(RELEASE_TAG)
  );
  for (let d = 0; d < indexed.length; d++) {
    const p = indexed[d].part;
    if (isAllPlanStepsComplete(p)) {
      if (messageHasReleaseTag) continue;
      return {
        confinedParts: stripReleaseTagFromIndexedParts(indexed.slice(0, d)),
        releasedParts: stripReleaseTagFromIndexedParts(indexed.slice(d)),
      };
    }
  }

  const lastReleaseBlockingToolIndex = findLastReleaseBlockingToolIndex(indexed);

  // Find the text part that contains <release/>
  for (let i = 0; i < indexed.length; i++) {
    const { part } = indexed[i];
    if (part?.type === "text" && typeof part.text === "string" && part.text.includes(RELEASE_TAG)) {
      const cleanedIndexed = stripReleaseTagFromIndexedParts(indexed);

      if (lastReleaseBlockingToolIndex > i) {
        const releaseStartIndex = lastReleaseBlockingToolIndex + 1;
        return {
          confinedParts: cleanedIndexed.filter(({ index }) => index < releaseStartIndex),
          releasedParts: cleanedIndexed.filter(({ index }) => index >= releaseStartIndex),
        };
      }

      // Split at this part. Text before the tag stays confined, text after is released.
      const tagPos = part.text.indexOf(RELEASE_TAG);
      const textBefore = cleanAssistantText(part.text.slice(0, tagPos));
      const textAfter = cleanAssistantText(part.text.slice(tagPos + RELEASE_TAG.length));

      const confined = indexed.slice(0, i);
      // If there's text before the tag in this same part, add it to confined
      if (textBefore) {
        confined.push({ part: { ...part, text: textBefore }, index: indexed[i].index });
      }

      const released: { part: any; index: number }[] = [];
      // If there's text after the tag in this same part, start released with it
      if (textAfter) {
        released.push({ part: { ...part, text: textAfter }, index: indexed[i].index });
      }
      // Everything after this part is released
      released.push(...indexed.slice(i + 1));

      return {
        confinedParts: stripReleaseTagFromIndexedParts(confined),
        releasedParts: stripReleaseTagFromIndexedParts(released),
      };
    }
  }

  // No <release/> found
  if (hasToolWork) {
    // Conservative fallback — confine tool work even for historical reloads
    return { confinedParts: stripReleaseTagFromIndexedParts(indexed), releasedParts: [] };
  }

  // Done streaming or no tool work — show everything at full size (fallback)
  return { confinedParts: [], releasedParts: stripReleaseTagFromIndexedParts(indexed) };
}

const PurePreviewMessage = ({
  chatId,
  message,
  liveParts,
  vote,
  isLoading,
  setMessages,
  reload,
  isReadonly,
  requiresScrollPadding,
  chartRef,
  append,
  isLastMessage = false,
  subAgentMessageIdsRef,
  messageSkillsRef,
  viewMode,
  initialViewMode,
  allMessages,
  isAlertsRoute = false,
  alertStartCellIndex,
  onSaveAsSkill,
}: {
  chatId: string;
  message: UIMessage;
  liveParts?: any[];
  vote: Vote | undefined;
  isLoading: boolean;
  setMessages: UseChatHelpers["setMessages"];
  reload: UseChatHelpers["reload"];
  isReadonly: boolean;
  requiresScrollPadding: boolean;
  chartRef?: MutableRefObject<TradingChartRef | null>;
  append?: UseChatHelpers["append"];
  isLastMessage?: boolean;
  subAgentMessageIdsRef?: MutableRefObject<Set<string>>;
  messageSkillsRef?: MutableRefObject<Map<string, string[]>>;
  viewMode?: string;
  /** Session baseline from page load — used for alert-page lock detection. */
  initialViewMode?: string;
  allMessages?: UIMessage[];
  /** True on /alerts/[id] — locks tool chrome to alert/signal. */
  isAlertsRoute?: boolean;
  alertStartCellIndex?: number | null;
  /** Turn this chat into a reusable skill (renders a "Save as skill" action
   *  under assistant messages). Undefined = hidden. */
  onSaveAsSkill?: () => void;
}) => {
  const { artifact, setArtifact } = useArtifact();
  const [completedContinues, setCompletedContinues] = useState<Set<string>>(
    new Set()
  );
  const [expandedThinkingSections, setExpandedThinkingSections] = useState<Set<string>>(
    new Set()
  );
  const [expandedUserTextPartIds, setExpandedUserTextPartIds] = useState<Set<string>>(
    new Set()
  );
  const confinedEndRef = useRef<HTMLDivElement>(null);
  const toolTelemetrySeenRef = useRef<Set<string>>(new Set());
  const reasoningTelemetrySeenRef = useRef<Set<string>>(new Set());
  const disableConfinement = artifact.isVisible || !artifact.hideSteps;
  const messageIsStreaming = isLoading && isLastMessage;
  const hasAuthoritativeLiveParts =
    message.role === "assistant" &&
    Array.isArray(liveParts) &&
    liveParts.length > 0;
  const rawParts = useMemo(
    () =>
      hasAuthoritativeLiveParts
        ? liveParts
        : Array.isArray(message.parts)
          ? message.parts
          : [],
    [hasAuthoritativeLiveParts, liveParts, message.parts],
  );

  const lockedViewMode =
    isAlertsRoute &&
    (initialViewMode === "alert" || initialViewMode === "signal")
      ? initialViewMode
      : null;

  const resolveToolViewMode = (partIndex: number) =>
    effectiveViewModeAtTool({
      messages: allMessages ?? [message],
      targetMessageId: message.id,
      targetPartIndex: partIndex,
      liveParts: hasAuthoritativeLiveParts ? liveParts : undefined,
      lockedViewMode,
    });

  useEffect(() => {
    if (!isLastMessage) {
      _liveDisplayStateCache.delete(chatId);
    }
    return () => {
      _liveDisplayStateCache.delete(chatId);
    };
  }, [chatId, isLastMessage]);

  const {
    displayParts,
    renderOrderChanged,
    liveDisplayDebug,
    removedReasoningDuplicates,
  } = useMemo(() => {
    if (hasAuthoritativeLiveParts) {
      const deduped = dedupeReasoningDisplayParts(rawParts);
      return {
        displayParts: deduped.displayParts,
        renderOrderChanged: deduped.removedReasoningDuplicates > 0,
        liveDisplayDebug: undefined,
        removedReasoningDuplicates: deduped.removedReasoningDuplicates,
      };
    }

    const cachedState = _liveDisplayStateCache.get(chatId);
    const shouldBuild =
      message.role === "assistant" &&
      rawParts.some((part: any) => part?.type === "tool-invocation") &&
      (messageIsStreaming || Boolean(cachedState));

    const {
      displayParts: nextDisplayParts,
      nextState,
      changed,
      debug,
      removedReasoningDuplicates: nextRemovedReasoningDuplicates = 0,
    } = buildLiveDisplayParts(rawParts, cachedState, shouldBuild);

    if (nextState) {
      _liveDisplayStateCache.set(chatId, nextState);
    } else {
      _liveDisplayStateCache.delete(chatId);
    }

    return {
      displayParts: nextDisplayParts,
      renderOrderChanged: changed,
      liveDisplayDebug: debug,
      removedReasoningDuplicates: nextRemovedReasoningDuplicates,
    };
  }, [chatId, hasAuthoritativeLiveParts, message.role, messageIsStreaming, rawParts]);

  useEffect(() => {
    if (
      hasAuthoritativeLiveParts ||
      message.role !== "assistant" ||
      !rawParts.some((part: any) => part?.type === "tool-invocation")
    ) {
      return;
    }

    const summarizeParts = (parts: any[]) =>
      parts.map((part: any) => {
        if (part?.type === "text") {
          return `text:${String(part?.text ?? "").slice(0, 80)}`;
        }
        if (part?.type === "tool-invocation") {
          return `tool:${part?.toolInvocation?.toolName ?? "unknown"}:${part?.toolInvocation?.state ?? "unknown"}`;
        }
        return String(part?.type ?? "unknown");
      });

    if (!renderOrderChanged && !liveDisplayDebug?.preservedRawOrder) {
      return;
    }

    debugLog("stream-order", {
      event: liveDisplayDebug?.preservedRawOrder
        ? "preserved_raw_order"
        : "render_reordered",
      chatId,
      messageId: message.id,
      isLoading,
      isLastMessage,
      messageIsStreaming,
      rawOrder: summarizeParts(rawParts),
      displayOrder: summarizeParts(displayParts),
      rawTextPreview: String(liveDisplayDebug?.rawText ?? "").slice(0, 160),
      deltaPreview: String(liveDisplayDebug?.delta ?? "").slice(0, 160),
      activeTextIndexBefore: liveDisplayDebug?.activeTextIndexBefore ?? null,
      activeTextIndexAfter: liveDisplayDebug?.activeTextIndexAfter ?? null,
      preservedRawOrderReason:
        liveDisplayDebug?.preservedRawOrderReason ?? null,
    });
  }, [
    chatId,
    displayParts,
    hasAuthoritativeLiveParts,
    isLoading,
    isLastMessage,
    liveDisplayDebug,
    message.id,
    message.role,
    messageIsStreaming,
    rawParts,
    renderOrderChanged,
  ]);

  useEffect(() => {
    if (message.role !== "assistant" || !removedReasoningDuplicates) return;

    const rawReasoningCount = rawParts.filter(
      (part: any) => part?.type === "reasoning",
    ).length;
    const displayReasoningCount = displayParts.filter(
      (part: any) => part?.type === "reasoning",
    ).length;
    const toolCount = displayParts.filter(
      (part: any) => part?.type === "tool-invocation",
    ).length;
    const telemetryKey = `${message.id}:reasoning-deduped`;

    if (reasoningTelemetrySeenRef.current.has(telemetryKey)) return;
    reasoningTelemetrySeenRef.current.add(telemetryKey);

    telemetry.emit("reasoning.display_deduped", {
      session_id: chatId,
      message_id: message.id,
      raw_reasoning_count: rawReasoningCount,
      display_reasoning_count: displayReasoningCount,
      removed_reasoning_duplicates: removedReasoningDuplicates,
      tool_count: toolCount,
      has_step_start_markers: rawParts.some(
        (part: any) => part?.type === "step-start",
      ),
      is_last_message: isLastMessage,
      is_streaming: messageIsStreaming,
      view_mode: viewMode,
    });
  }, [
    chatId,
    displayParts,
    isLastMessage,
    message.id,
    message.role,
    messageIsStreaming,
    rawParts,
    removedReasoningDuplicates,
    viewMode,
  ]);

  useEffect(() => {
    if (message.role !== "assistant") return;
    for (const part of displayParts) {
      if (part?.type !== "tool-invocation") continue;
      const invocation = part.toolInvocation;
      const toolName = invocation?.toolName || "unknown";
      const toolCallId = invocation?.toolCallId || invocation?.id || "unknown";
      const state = invocation?.state || "unknown";
      const result = invocation?.result;
      const supported = TELEMETRY_SUPPORTED_TOOLS.has(toolName);

      const emitOnce = (suffix: string, eventType: Parameters<typeof telemetry.emit>[0], payload: Record<string, unknown>) => {
        const key = `${message.id}:${toolCallId}:${state}:${suffix}`;
        if (toolTelemetrySeenRef.current.has(key)) return;
        toolTelemetrySeenRef.current.add(key);
        telemetry.emit(eventType, {
          session_id: chatId,
          message_id: message.id,
          tool_name: toolName,
          tool_call_id: toolCallId,
          state,
          is_last_message: isLastMessage,
          is_streaming: isLoading,
          view_mode: viewMode,
          ...payload,
        });
      };

      if (!supported) {
        emitOnce("unsupported", "tool.unsupported_dropped", {
          reason: "not_in_supported_tools",
        });
        continue;
      }

      if (state === "call") {
        emitOnce("loading", "tool.card_rendered", {
          status: "loading",
          has_args: Boolean(invocation?.args),
        });
      }

      if (state === "result") {
        const outputs = result?.execution_result?.outputs || [];
        const outputTypes = Array.isArray(outputs)
          ? outputs.map((output: any) => output?.type || output?.output_type || "unknown").slice(0, 20)
          : [];
        const hasOutputError = Array.isArray(outputs)
          ? outputs.some((output: any) => output?.output_type === "error")
          : false;
        const status = result?.status;
        const error = result?.error;
        const errorMessage = result?.error_message;
        const stage = result?.stage;
        const isUserCanceled = errorMessage === "User requested stop" && stage === "user_cancel";
        const isMaxIterations = errorMessage === "Max agent turns reached" && stage === "max_iterations_guard";
        const isNotebookBusyResult = isNotebookBusy(result);
        const showsGenericError =
          (((status === "error" || error) && !isUserCanceled && !isMaxIterations && !isNotebookBusyResult) ||
            (!isNotebookBusyResult && hasOutputError));

        emitOnce("success", "tool.card_rendered", {
          status: showsGenericError ? "generic_error" : "success",
          result_status: status,
          output_count: outputTypes.length,
          output_types: outputTypes,
          has_output_error: hasOutputError,
        });

        if (showsGenericError) {
          emitOnce("generic_error", "tool.generic_error_shown", {
            result_status: status,
            stage,
            error_message: String(errorMessage || error || "tool result error").slice(0, 500),
            has_output_error: hasOutputError,
            output_types: outputTypes,
          });
        }
      }
    }
  }, [chatId, displayParts, isLastMessage, isLoading, message.id, message.role, viewMode]);

  // Determine if this assistant message has intermediate tool work
  // ANY tool invocation counts — not just add_cell/replace_cell
  const hasToolWork = message.role === "assistant" && displayParts.some(
    (p: any) => p?.type === "tool-invocation"
  );

  // Compute the release split
  const { confinedParts, releasedParts } = useMemo(
    () =>
      message.role === "assistant"
        ? splitAtRelease(
            displayParts,
            messageIsStreaming,
            hasToolWork,
            disableConfinement
          )
        : { confinedParts: [], releasedParts: displayParts.map((part: any, index: number) => ({ part, index })) },
    [displayParts, message.role, messageIsStreaming, hasToolWork, disableConfinement]
  );

  const thinkingSections = useMemo(
    () => buildThinkingSections(confinedParts, releasedParts.length > 0, messageIsStreaming),
    [confinedParts, releasedParts.length, messageIsStreaming]
  );
  const latestThinkingSectionId =
    thinkingSections[thinkingSections.length - 1]?.id ?? null;

  useEffect(() => {
    if (thinkingSections.length === 0) {
      setExpandedThinkingSections(new Set());
      return;
    }

    if (latestThinkingSectionId) {
      setExpandedThinkingSections(new Set([latestThinkingSectionId]));
    }
  }, [thinkingSections.length, latestThinkingSectionId]);

  // Auto-scroll the confined container to bottom as new content streams in
  useEffect(() => {
    if (thinkingSections.length > 0 && releasedParts.length === 0 && confinedEndRef.current) {
      confinedEndRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [thinkingSections.length, latestThinkingSectionId, releasedParts.length, confinedParts.length]);

  // Create continue function for max iterations case
  const handleContinue = (toolCallId: string) => {
    if (append) {
      // Mark this continue as completed
      setCompletedContinues((prev) => new Set(prev).add(toolCallId));

      append({
        role: "user",
        content: "continue",
      });
    }
  };

  const toggleUserTextPart = (partId: string) => {
    setExpandedUserTextPartIds((prev) => {
      const next = new Set(prev);
      if (next.has(partId)) {
        next.delete(partId);
      } else {
        next.add(partId);
      }
      return next;
    });
  };

  return (
    <AnimatePresence>
      <motion.div
        id={`msg-${message.id}`}
        data-testid={`message-${message.role}`}
        data-message-id={message.id}
        className="w-full mx-auto max-w-3xl group/message min-w-0"
        initial={{ y: 5, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        data-role={message.role}
      >
        <div className="flex flex-col w-full group-data-[role=user]/message:ml-auto group-data-[role=user]/message:max-w-2xl group-data-[role=user]/message:w-fit my-2 min-w-0">
          <div
            className={cn("flex flex-col gap-3 w-full min-w-0", {
              "min-h-24": message.role === "assistant" && requiresScrollPadding,
            })}
          >
            {/* Check for error card first */}
            {message.experimental_attachments?.some(
              (att) => att.name === "error-card"
            ) &&
              (message as any).errorData && (
                <ErrorCard
                  errorMessage={(message as any).errorData.errorMessage}
                  errorType={(message as any).errorData.errorType}
                  errorSubtype={(message as any).errorData.errorSubtype}
                />
              )}

            {message.experimental_attachments &&
              message.experimental_attachments.length > 0 &&
              !message.experimental_attachments.some(
                (att) => att.name === "error-card"
              ) && (
                <div
                  data-testid={`message-attachments`}
                  className="flex flex-row justify-end gap-2"
                >
                  {message.experimental_attachments.map((attachment) => (
                    <PreviewAttachment
                      key={attachment.url}
                      attachment={attachment}
                    />
                  ))}
                </div>
              )}

            {/* Confined thinking steps */}
            {thinkingSections.length > 0 && (
              <div className="flex flex-col rounded-lg border border-white/[0.08] bg-zinc-900/70 overflow-hidden">
                {thinkingSections.map((section, sectionIndex) => {
                  const isExpanded = expandedThinkingSections.has(section.id);
                  const isCurrentSection = sectionIndex === thinkingSections.length - 1;
                  const isActive = isCurrentSection && !section.isComplete;
                  const toggleSection = () =>
                    setExpandedThinkingSections((prev) => {
                      if (prev.has(section.id)) {
                        return new Set();
                      }
                      return new Set([section.id]);
                    });

                  return (
                    <div
                      key={section.id}
                      className={cn(
                        "overflow-hidden transition-colors border-b border-white/[0.08] last:border-b-0",
                        isActive || isExpanded ? "bg-white/[0.035]" : ""
                      )}
                    >
                      <button
                        type="button"
                        onClick={toggleSection}
                        className={cn(
                          "flex w-full items-center gap-2.5 px-3.5 py-3 text-left transition-colors hover:bg-white/[0.03]",
                          isActive || isExpanded ? "border-l-2 border-emerald-400/60 ml-[-1px] bg-white/[0.04]" : ""
                        )}
                      >
                        <div className="flex h-4 w-4 shrink-0 items-center justify-center">
                          {section.isComplete ? (
                            <Check className="h-3.5 w-3.5 text-emerald-400" strokeWidth={2.5} />
                          ) : isCurrentSection ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin text-emerald-300/70" strokeWidth={2} />
                          ) : (
                            <div className="h-1 w-1 rounded-full bg-white/30" />
                          )}
                        </div>
                        <span
                          className={cn(
                            "min-w-0 flex-1 truncate text-sm leading-snug",
                            section.isComplete
                              ? "text-white/80"
                              : "text-white font-medium"
                          )}
                        >
                          {section.title}
                        </span>
                        <div className="flex shrink-0 items-center">
                          {isActive && !artifact.isVisible && (
                            <>
                              <button
                                type="button"
                                aria-label="View Full Analysis"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setArtifact((prev) => ({ ...prev, isVisible: true }));
                                }}
                                className="mr-2 md:mr-3 inline-flex items-center justify-center gap-1.5 rounded-md border border-green-500/20 bg-green-500/10 px-2 py-1 md:px-2.5 text-xs font-medium text-green-400 transition-colors hover:bg-green-500/15 hover:text-green-300"
                              >
                                <PanelRightOpen className="h-3.5 w-3.5 shrink-0" />
                                <span className="hidden md:inline">
                                  View Full Analysis
                                </span>
                              </button>
                              <div className="h-3 w-px bg-white/10 mr-2 md:mr-2.5" />
                            </>
                          )}
                          <ChevronDown
                            className={cn(
                              "h-3.5 w-3.5 transition-transform duration-150",
                              isExpanded ? "text-white/65" : "text-white/25",
                              isExpanded ? "rotate-180" : ""
                            )}
                            strokeWidth={2}
                          />
                        </div>
                      </button>

                      {isExpanded && (
                        <div className="px-2 pb-2 pt-1.5 space-y-3 text-sm">
                          {section.parts.map(({ part, index }) => {
                            const { type } = part;
                            const key = `confined-${message.id}-part-${index}`;

                            if (type === "reasoning") {
                              const hasFollowingNonReasoning =
                                displayParts
                                  ?.slice(index + 1)
                                  .some((p: any) => p.type !== "reasoning") ?? false;
                              return (
                                <MessageReasoning
                                  key={key}
                                  isLoading={isLoading && !hasFollowingNonReasoning}
                                  reasoning={part.reasoning}
                                />
                              );
                            }

                            if (type === "text") {
                              return (
                                <div key={key} className="min-w-0 text-white/75">
                                  <Markdown>{sanitizeText(part.text)}</Markdown>
                                </div>
                              );
                            }

                            if (type === "tool-invocation") {
                              const { toolInvocation } = part;
                              const { toolName, toolCallId, state } = toolInvocation;
                              const supportedTools = [
                                "add_cell", "replace_cell", "delete_cells", "restart_notebook", "create_plan",
                                "update_plan", "unified_search", "get_documentation",
                                "get_visual_templates", "get_cell_details", "create_new_run",
                                "execute_all_cells", "add_plan_step", "delete_plan_step",
                                "add_drawings", "set_viewport", "delete_drawings", "fast_forward",
                                "await_user_execution", "user_message", "ticker_search",
                                "error_logging_tool", "generate_summary", "ask_user",
                                "get_xynth_market_screener_docs", "consult_planning_agent", "search_docs",
                                "upload_alert", "delete_alert", "edit_alert", "fork_alert", "create_alert",
                                "switch_mode", "propose_skill", "get_skill",
                              ];

                              if (PLAN_TOOL_NAMES.has(toolName) && toolName !== "consult_planning_agent") return null;

                              if (state === "call" && supportedTools.includes(toolName)) {
                                return (
                                  <div key={toolCallId} className="min-w-0 w-full">
                                    <ToolDisplay
                                      toolName={toolName}
                                      status="loading"
                                      chatId={chatId}
                                      args={toolInvocation.args}
                                      isReadonly={isReadonly}
                                      toolCallId={toolCallId}
                                      onContinue={() => handleContinue(toolCallId)}
                                      isLastMessage={isLastMessage}
                                      isCompleted={completedContinues.has(toolCallId)}
                                      isArtifactVisible={false}
                                      viewMode={resolveToolViewMode(index)}
                                      alertStartCellIndex={alertStartCellIndex}
                                    />
                                  </div>
                                );
                              }

                              if (state === "result" && supportedTools.includes(toolName)) {
                                const { result } = toolInvocation;
                                const errorMessage = result?.error_message;
                                const stage = result?.stage;
                                const rstatus = result?.status;
                                const error = result?.error;
                                const isUserCanceled = errorMessage === "User requested stop" && stage === "user_cancel";
                                const isMaxIterations = errorMessage === "Max agent turns reached" && stage === "max_iterations_guard";
                                const isNotebookBusyResult = isNotebookBusy(result);

                                if ((rstatus === "error" || error) && !isUserCanceled && !isMaxIterations && !isNotebookBusyResult && toolName !== "propose_skill") {
                                  return <GenericError key={toolCallId} viewMode={resolveToolViewMode(index)} />;
                                }
                                if (!isNotebookBusyResult && result.execution_result?.outputs?.some((output: any) => output.output_type === "error")) {
                                  return <GenericError key={toolCallId} viewMode={resolveToolViewMode(index)} />;
                                }

                                return (
                                  <div key={toolCallId} className="min-w-0 w-full">
                                    <ToolDisplay
                                      toolName={toolName}
                                      status="success"
                                      chartRef={chartRef}
                                      chatId={chatId}
                                      result={result}
                                      isReadonly={isReadonly}
                                      args={toolInvocation.args}
                                      toolCallId={toolCallId}
                                      onContinue={() => handleContinue(toolCallId)}
                                      isLastMessage={isLastMessage}
                                      isCompleted={completedContinues.has(toolCallId)}
                                      isArtifactVisible={false}
                                      viewMode={resolveToolViewMode(index)}
                                      alertStartCellIndex={alertStartCellIndex}
                                      isStreaming={isLoading}
                                    />
                                  </div>
                                );
                              }
                              return null;
                            }
                            return null;
                          })}
                          {isCurrentSection && releasedParts.length === 0 && (
                            <div ref={confinedEndRef} />
                          )}
                          {/* Bottom collapse button — visible when section has many parts */}
                          {section.parts.length > 3 && (
                            <button
                              type="button"
                              onClick={toggleSection}
                              className="mt-0.5 inline-flex self-center items-center justify-center gap-1 px-1 py-0.5 text-xs text-white/40 transition-colors hover:text-white/65"
                            >
                              <ChevronDown className="h-3.5 w-3.5 rotate-180" strokeWidth={2} />
                              <span>Collapse</span>
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Released parts — render at full size */}
            {releasedParts.map(({ part, index }) => {
              const { type } = part;
              const key = `message-${message.id}-part-${index}`;

              if (type === "reasoning") {
                // Stop showing the reasoning spinner as soon as any non-reasoning
                // part follows (e.g., text, tool-invocation, etc.). This aligns
                // with the Vercel AI SDK Data Stream Protocol where reasoning (g:)
                // is followed by other parts (0:, 9:, a:, ...).
                const hasFollowingNonReasoning =
                  displayParts
                    ?.slice(index + 1)
                    .some((p) => p.type !== "reasoning") ?? false;
                const isReasoningLoading =
                  isLoading && !hasFollowingNonReasoning;

                return (
                  <MessageReasoning
                    key={key}
                    isLoading={isReasoningLoading}
                    reasoning={part.reasoning}
                  />
                );
              }

              if (type === "text") {
                const sanitizedText = sanitizeText(part.text);
                const userTextPartId = `${message.id}:${index}`;
                const isCollapsibleUserText =
                  message.role === "user" && shouldCollapseUserMessageText(sanitizedText);
                const isUserTextExpanded = expandedUserTextPartIds.has(userTextPartId);
                const displayedText =
                  isCollapsibleUserText && !isUserTextExpanded
                    ? getCollapsedUserMessageText(sanitizedText)
                    : sanitizedText;

                const skillArtifactIds: string[] =
                  message.role === "user"
                    ? ((message as any).skillIds as string[] | undefined) ||
                      ((message as any).skillId
                        ? [(message as any).skillId as string]
                        : undefined) ||
                      messageSkillsRef?.current?.get(message.id) ||
                      []
                    : [];

                return (
                  <div
                    key={key}
                    className={cn("flex flex-col gap-1 min-w-0 animate-polling-reveal", {
                      "items-end": message.role === "user",
                    })}
                  >
                    {message.role === "user" && (
                      (message as any).subAgents ||
                      subAgentMessageIdsRef?.current?.has(message.id)
                    ) && (
                      <div className="flex justify-end mb-1">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-500/10 text-amber-400 border border-amber-500/20">
                          <GitFork size={10} />
                          Parallel Agents
                        </span>
                      </div>
                    )}
                    {skillArtifactIds.length > 0 && (
                      <div className="flex w-full min-w-0 items-center justify-end gap-1">
                        {skillArtifactIds.map((sid) => (
                          <SkillArtifactCard key={sid} skillId={sid} />
                        ))}
                      </div>
                    )}
                    <div
                      className={cn("flex flex-row gap-2 items-start min-w-0", {
                        "justify-end": message.role === "user",
                      })}
                    >
                      <div
                        data-testid="message-content"
                        className={cn("flex flex-col gap-4 min-w-0", {
                          "bg-primary text-primary-foreground px-3 py-2 rounded-xl max-w-full overflow-hidden break-words [overflow-wrap:anywhere]":
                            message.role === "user",
                        })}
                      >
                        <Markdown enableCellCitations={message.role === "assistant"} citationWorkflowView="current-run">
                          {displayedText}
                        </Markdown>
                        {isCollapsibleUserText && (
                          <button
                            type="button"
                            aria-expanded={isUserTextExpanded}
                            onClick={() => toggleUserTextPart(userTextPartId)}
                            className="inline-flex w-fit items-center gap-1 rounded-md px-1 py-0.5 text-xs font-medium text-primary-foreground/75 transition-colors hover:text-primary-foreground"
                          >
                            <span>{isUserTextExpanded ? "Show less" : "See more"}</span>
                            <ChevronDown
                              className={cn(
                                "h-3.5 w-3.5 transition-transform duration-150",
                                isUserTextExpanded ? "rotate-180" : ""
                              )}
                              strokeWidth={2}
                            />
                          </button>
                        )}
                      </div>
                    </div>
                    {message.createdAt && (!isLastMessage || !isLoading) && (
                      <div
                        className={cn("text-xs text-muted-foreground", {
                          "ml-auto": message.role === "user",
                        })}
                      >
                        {new Intl.DateTimeFormat("en-GB", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: false,
                        }).format(new Date(message.createdAt))}
                      </div>
                    )}
                  </div>
                );
              }

              if (type === "tool-invocation") {
                const { toolInvocation } = part;
                const { toolName, toolCallId, state } = toolInvocation;
                const supportedTools = [
                  "add_cell",
                  "replace_cell",
                  "edit_cell",
                  "delete_cells",
                  "restart_notebook",
                  "create_plan",
                  "update_plan",
                  "unified_search",
                  "get_documentation",
                  "get_visual_templates",
                  "get_cell_details",
                  "create_new_run",
                  "execute_all_cells",
                  "add_plan_step",
                  "delete_plan_step",
                  "add_drawings",
                  "set_viewport",
                  "delete_drawings",
                  "fast_forward",
                  "await_user_execution",
                  "ask_user",
                  "user_message",
                  "ticker_search",
                  "error_logging_tool",
                  "generate_summary",
                  "get_xynth_market_screener_docs",
                  "consult_planning_agent",
                  "search_docs",
                  "upload_alert",
                  "delete_alert",
                  "edit_alert",
                  "fork_alert",
                  "create_alert",
                  "switch_mode",
                  "propose_skill",
                  "get_skill",
                ];

                if (state === "call") {
                  const { args } = toolInvocation;

                  if (supportedTools.includes(toolName)) {
                    return (
                      <div key={toolCallId} className="min-w-0 w-full animate-polling-reveal">
                        <ToolDisplay
                          toolName={toolName}
                          status="loading"
                          chatId={chatId}
                          args={args}
                          isReadonly={isReadonly}
                          toolCallId={toolCallId}
                          onContinue={() => handleContinue(toolCallId)}
                          isLastMessage={isLastMessage}
                          isCompleted={completedContinues.has(toolCallId)}
                          isArtifactVisible={artifact.isVisible}
                          viewMode={resolveToolViewMode(index)}
                          alertStartCellIndex={alertStartCellIndex}
                        />
                      </div>
                    );
                  }

                  return null;
                }

                if (state === "result") {
                  const { result } = toolInvocation;
                  // DEBUG: Log tool result arrival with output types
                  const _outputs = result?.execution_result?.outputs || [];
                  const _outputTypes = _outputs.map((o: any) => o?.type || o?.output_type || '?');
                  const _subAgentCount = _outputs.filter((o: any) => o?.type === 'sub_agent').length;
                  if (_subAgentCount > 0) {
                    _outputs.filter((o: any) => o?.type === 'sub_agent').forEach((o: any, i: number) => {
                    });
                  }

                  // Safe property access with null checks
                  const errorMessage = result?.error_message;
                  const stage = result?.stage;
                  const status = result?.status;
                  const error = result?.error;

                  // Check if this is a user cancellation or max iterations vs actual error
                  const isUserCanceled =
                    errorMessage === "User requested stop" &&
                    stage === "user_cancel";
                  const isMaxIterations =
                    errorMessage === "Max agent turns reached" &&
                    stage === "max_iterations_guard";
                  const isNotebookBusyResult = isNotebookBusy(result);

                  if (
                    (status === "error" || error) &&
                    !isUserCanceled &&
                    !isMaxIterations &&
                    !isNotebookBusyResult &&
                    toolName !== "propose_skill"
                  ) {
                    return <GenericError key={toolCallId} viewMode={resolveToolViewMode(index)} />;
                  }

                  // Check for error outputs in execution_result
                  if (
                    !isNotebookBusyResult &&
                    result.execution_result?.outputs?.some(
                      (output: any) => output.output_type === "error"
                    )
                  ) {
                    return <GenericError key={toolCallId} viewMode={resolveToolViewMode(index)} />;
                  }

                  if (supportedTools.includes(toolName)) {
                    return (
                      <div key={toolCallId} className="min-w-0 w-full animate-polling-reveal">
                        <ToolDisplay
                          toolName={toolName}
                          status="success"
                          chartRef={chartRef}
                          chatId={chatId}
                          result={result}
                          isReadonly={isReadonly}
                          args={toolInvocation.args}
                          toolCallId={toolCallId}
                          onContinue={() => handleContinue(toolCallId)}
                          isLastMessage={isLastMessage}
                          isCompleted={completedContinues.has(toolCallId)}
                          isArtifactVisible={artifact.isVisible}
                          viewMode={resolveToolViewMode(index)}
                          alertStartCellIndex={alertStartCellIndex}
                          isStreaming={isLoading}
                        />
                      </div>
                    );
                  }

                  return null;
                }
              }
            })}

            {!isReadonly && (() => {
              const messageIndex = allMessages?.findIndex((m) => m.id === message.id) ?? -1;
              return (
                <MessageActions
                  key={`action-${message.id}`}
                  chatId={chatId}
                  message={message}
                  vote={vote}
                  isLoading={isLoading}
                  onSaveAsSkill={onSaveAsSkill}
                  messageIndex={messageIndex >= 0 ? messageIndex : undefined}
                />
              );
            })()}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export const PreviewMessage = memo(
  PurePreviewMessage,
  (prevProps, nextProps) => {
    if (prevProps.isLoading !== nextProps.isLoading) return false;
    if (prevProps.message.id !== nextProps.message.id) return false;
    if (prevProps.requiresScrollPadding !== nextProps.requiresScrollPadding)
      return false;
    if (!equal(prevProps.liveParts, nextProps.liveParts)) return false;
    if (!equal(prevProps.message.parts, nextProps.message.parts)) return false;
    if (!equal(prevProps.vote, nextProps.vote)) return false;
    if (prevProps.viewMode !== nextProps.viewMode) return false;
    if (prevProps.initialViewMode !== nextProps.initialViewMode) return false;
    if (prevProps.isAlertsRoute !== nextProps.isAlertsRoute) return false;
    if (!equal(prevProps.allMessages, nextProps.allMessages)) return false;
    if (prevProps.alertStartCellIndex !== nextProps.alertStartCellIndex) return false;
    return true;
  }
);

export const ThinkingMessage = () => {
  const role = "assistant";

  return (
    <motion.div
      data-testid="message-assistant-loading"
      className="w-full mx-auto max-w-3xl group/message"
      initial={{ y: 5, opacity: 0 }}
      animate={{ y: 0, opacity: 1, transition: { delay: 0.3 } }}
      data-role={role}
    >
      <div
        className={cx(
          "flex gap-4 group-data-[role=user]/message:px-3 w-full group-data-[role=user]/message:w-fit group-data-[role=user]/message:ml-auto group-data-[role=user]/message:max-w-2xl group-data-[role=user]/message:py-2 rounded-xl",
          {
            "group-data-[role=user]/message:bg-muted": true,
          }
        )}
      >
        <div className="flex flex-col gap-2 w-full">
          <div className="flex items-center text-zinc-600 text-base mt-2">
            <span>Working</span>
            <div className="flex ml-2">
              <motion.div
                className="w-1 h-1 bg-muted-foreground rounded-full"
                animate={{ opacity: [0.3, 1, 0.3] }}
                transition={{
                  duration: 1.2,
                  repeat: Infinity,
                  delay: 0,
                }}
              />
              <motion.div
                className="w-1 h-1 bg-muted-foreground rounded-full ml-1"
                animate={{ opacity: [0.3, 1, 0.3] }}
                transition={{
                  duration: 1.2,
                  repeat: Infinity,
                  delay: 0.4,
                }}
              />
              <motion.div
                className="w-1 h-1 bg-muted-foreground rounded-full ml-1"
                animate={{ opacity: [0.3, 1, 0.3] }}
                transition={{
                  duration: 1.2,
                  repeat: Infinity,
                  delay: 0.8,
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};
